import { useCallback } from 'react';
import {
  createBlock,
  findBlock,
  flattenBlocks,
  cleanupEmptyContainers,
  ensureCalloutsHaveChildren,
} from '../../../utils/blockUtils';
import {
  wrapBlocksInCallout,
  unwrapCallout,
  wrapTextSelectionInCallout,
} from '../../../utils/calloutWrapUtils';
import { focusEditable } from '../../../utils/focusUtils';
import { EDITABLE_TYPES } from '../../../utils/editorConstants';

/** Wrap selection → callout, or lift callout children and remove the shell. */
export default function useCalloutHandlers({ blocksRef, commit }) {
  /**
   * Convert selection → callout. Prefer multi-selected sibling ids; else a
   * partial text selection split; else wrap the single block.
   * Returns the new callout id, or null.
   */
  const handleWrapInCallout = useCallback(
    (blockId, { ids, selectionSplit } = {}) => {
      const current = blocksRef.current;
      const wrapIds =
        Array.isArray(ids) && ids.length > 0 ? ids : blockId ? [blockId] : [];
      if (wrapIds.length === 0) return null;

      let result = null;
      if (wrapIds.length > 1) {
        result = wrapBlocksInCallout(current, wrapIds);
      } else if (selectionSplit) {
        result = wrapTextSelectionInCallout(current, wrapIds[0], selectionSplit);
        if (!result) result = wrapBlocksInCallout(current, wrapIds);
      } else {
        result = wrapBlocksInCallout(current, wrapIds);
      }

      if (!result) return null;
      const tree = ensureCalloutsHaveChildren(
        cleanupEmptyContainers(result.blocks)
      );
      commit(tree);

      const callout = findBlock(tree, result.calloutId);
      const firstEditable = callout
        ? flattenBlocks(callout.children || []).find((b) =>
            EDITABLE_TYPES.includes(b.type)
          )
        : null;
      if (firstEditable) focusEditable(firstEditable.id);
      return result.calloutId;
    },
    [commit, blocksRef]
  );

  /** Lift callout children and remove the shell. Returns focus id or null. */
  const handleUnwrapCallout = useCallback(
    (calloutId) => {
      const result = unwrapCallout(blocksRef.current, calloutId);
      if (!result) return null;
      const tree = ensureCalloutsHaveChildren(
        cleanupEmptyContainers(result.blocks)
      );
      // Unwrapping the last editable out of the doc shouldn't leave it empty
      let next = tree;
      if (!flattenBlocks(next).some((b) => EDITABLE_TYPES.includes(b.type))) {
        const fresh = createBlock('text');
        next = [...next, fresh];
        commit(next);
        focusEditable(fresh.id);
        return fresh.id;
      }
      commit(next);
      if (result.focusId) {
        const focusBlock = findBlock(next, result.focusId);
        if (focusBlock && EDITABLE_TYPES.includes(focusBlock.type)) {
          focusEditable(result.focusId);
        } else {
          const nested = flattenBlocks(focusBlock?.children || []).find((b) =>
            EDITABLE_TYPES.includes(b.type)
          );
          if (nested) focusEditable(nested.id);
        }
      }
      return result.focusId;
    },
    [commit, blocksRef]
  );

  return { handleWrapInCallout, handleUnwrapCallout };
}
